"use client"

import { useEffect, useState } from "react"
import { signIn } from "next-auth/react"
import { useRouter } from "next/navigation"
import { Pencil, Box, CircuitBoard, Clock } from "lucide-react"
import { Logo } from "@/components/Logo"

/** Apres la connexion : la demande au support du lien du mail (?support=<n>), sinon l'accueil */
function afterLogin(): string {
  const support = new URLSearchParams(window.location.search).get("support")
  return support && /^\d{1,9}$/.test(support) ? `/?support=${support}` : "/"
}

export default function LoginPage() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Pourquoi on arrive ici (CNX) : deconnecte apres 15 minutes d'inactivite, ou session expiree. Lu apres le
  // rendu : useSearchParams demanderait une frontiere Suspense a toute la page.
  const [notice, setNotice] = useState<string | null>(null)
  const [noticeWarn, setNoticeWarn] = useState(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("raison") === "inactivite") {
      const travail = params.get("travail")
      setNoticeWarn(travail === "navigateur" || travail === "perdu")
      setNotice(
        travail === "garde"
          ? "Vous avez été déconnecté après 15 minutes d'inactivité. Vos modifications non enregistrées ont été gardées : elles vous seront proposées après la connexion."
          : travail === "navigateur"
            ? "Vous avez été déconnecté après 15 minutes d'inactivité. La connexion au serveur était coupée : vos modifications non enregistrées sont gardées dans ce navigateur seulement. Reconnectez-vous ici pour les retrouver."
            : travail === "perdu"
              ? "Vous avez été déconnecté après 15 minutes d'inactivité. Le serveur ne répondait pas et ce navigateur manquait de place : vos modifications non enregistrées n'ont pas pu être gardées."
              : "Vous avez été déconnecté après 15 minutes d'inactivité. Reconnectez-vous pour continuer."
      )
    } else if (params.get("expired") === "1" || params.get("travail")) {
      const travail = params.get("travail")
      setNoticeWarn(travail === "navigateur" || travail === "perdu")
      setNotice(
        travail === "navigateur"
          ? "Votre session a expiré. Vos modifications non enregistrées sont gardées dans ce navigateur seulement : reconnectez-vous ici pour les retrouver."
          : travail === "perdu"
            ? "Votre session a expiré. Vos modifications non enregistrées n'ont pas pu être gardées."
            : params.get("expired") === "1"
              ? "Votre session a expiré. Reconnectez-vous pour continuer."
              : "Reconnectez-vous pour continuer."
      )
    }
  }, [])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const form = new FormData(e.currentTarget)
    const email = form.get("email") as string
    const password = form.get("password") as string

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
    })

    // Auth.js v5 : identifiants refuses = ok a true avec error renseigne
    if (result?.ok && !result.error) {
      router.push(afterLogin())
    } else {
      setError("Email ou mot de passe incorrect")
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel — navy profond */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B2A4A] flex-col justify-between p-12 relative overflow-hidden">
        {/* Grille pointillee (rappel canvas architecte) */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Decoration : cercles abstraits */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/[0.02]" />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/[0.02]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#2E86C1]/[0.08]" />
        </div>

        {/* Silhouette plan de maison SVG */}
        <svg className="absolute bottom-12 right-12 w-64 h-48 text-white opacity-[0.04]" viewBox="0 0 200 150" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <rect x="10" y="30" width="180" height="110" />
          <line x1="10" y1="80" x2="110" y2="80" />
          <line x1="110" y1="30" x2="110" y2="140" />
          <line x1="110" y1="100" x2="190" y2="100" />
          <rect x="45" y="95" width="30" height="45" strokeDasharray="4 3" />
          <circle cx="155" cy="65" r="4" />
          <circle cx="55" cy="55" r="4" />
          <circle cx="155" cy="120" r="4" />
        </svg>

        {/* Logo */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div>
              <Logo size="lg" onDark />
              <p className="mt-2 text-[10px] text-white/40 uppercase tracking-widest leading-none">Architecture &amp; électricité</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="relative space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-white leading-tight">
              Concevez vos plans<br />d&apos;architecture et d&apos;électricité
            </h2>
            <p className="text-white/50 mt-3 text-sm leading-relaxed">
              Dessinez vos plans de maison, placez vos circuits<br />électriques, et visualisez le tout en 3D.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: Pencil, text: "Dessinez vos plans d\u2019architecture en 2D" },
              { icon: CircuitBoard, text: "Placez vos circuits et symboles électriques" },
              { icon: Box, text: "Visualisez le résultat en 3D temps réel" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-white/70" />
                </div>
                <p className="text-sm text-white/70">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative">
          <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} archifacil — Architecture &amp; plans électriques</p>
        </div>
      </div>

      {/* Right panel — sign in */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F4F6F9]">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <Logo size="lg" />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 relative overflow-hidden">
            {/* Bordure top accent */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#1B4F72]" />

            <div className="mb-6">
              <h1 className="text-xl font-bold text-gray-900">Connexion</h1>
              <p className="text-gray-500 text-sm mt-1">Accédez à votre espace de conception</p>
            </div>

            {notice && !error && (
              <div role="status" className={`mb-4 p-3 flex gap-2 border rounded-lg text-sm ${noticeWarn ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-100 text-blue-900"}`}>
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <p>{notice}</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4F72]/20 focus:border-[#1B4F72]/60 transition-all placeholder:text-gray-400"
                  placeholder="jean@exemple.fr"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Mot de passe
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-xs text-[#1B4F72] hover:text-[#154360] hover:underline font-medium"
                  >
                    Mot de passe oublié ?
                  </a>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4F72]/20 focus:border-[#1B4F72]/60 transition-all placeholder:text-gray-400"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-[#1B4F72] text-white rounded-lg text-sm font-medium hover:bg-[#154360] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400">ou</span>
              </div>
            </div>

            <button
              onClick={() => signIn("google", { callbackUrl: afterLogin() })}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Se connecter avec Google
            </button>
          </div>

          <div className="text-center mt-4 space-y-2">
            <p className="text-sm text-gray-600">
              Pas encore de compte ?{" "}
              <a href="/register" className="text-[#1B4F72] hover:text-[#154360] hover:underline font-medium">
                Créer un compte
              </a>
            </p>
            <p className="text-xs text-gray-400">
              En vous connectant, vous acceptez les{" "}
              <a href="/cgu" className="underline hover:text-gray-600 transition-colors">
                conditions générales d&apos;utilisation
              </a>
              {" · "}
              <a href="/confidentialite" className="underline hover:text-gray-600 transition-colors">
                Données personnelles
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
